import type { CommunityPost, PaginationMeta } from '~/interfaces/community';

import { type MemberFeedPage, useMemberPagedFeed } from './useMemberPagedFeed';

const COMMUNITY_MEMBER_POSTS_PATH = '/community/member_posts';

const communityMemberPostsKeys = {
  all: ['community-member-posts'] as const,
  feed: (memberId: string) => [...communityMemberPostsKeys.all, memberId] as const,
};

const withPinnedFirst = (posts: CommunityPost[], pinnedPost?: CommunityPost | null): CommunityPost[] =>
  pinnedPost ? [pinnedPost, ...posts] : posts;

const fetchCommunityMemberPostsPage = async (
  memberId: string,
  cursor: string | undefined,
): Promise<MemberFeedPage<CommunityPost>> => {
  const params = new URLSearchParams({ id: memberId });
  if (cursor) params.set('cursor', cursor);

  const response = await fetch(`${COMMUNITY_MEMBER_POSTS_PATH}?${params}`);
  if (!response.ok) throw new Error('Failed to fetch community member posts');

  const data: { posts: CommunityPost[]; pinned_post?: CommunityPost | null; meta: PaginationMeta } =
    await response.json();
  return { items: withPinnedFirst(data.posts, data.pinned_post), meta: data.meta };
};

interface UseCommunityMemberPostsOptions {
  memberId: string;
  initialPosts?: CommunityPost[];
  initialPinnedPost?: CommunityPost | null;
  initialMeta?: PaginationMeta;
}

const useCommunityMemberPosts = ({
  memberId,
  initialPosts,
  initialPinnedPost,
  initialMeta,
}: UseCommunityMemberPostsOptions) => {
  const { items: posts, ...pagination } = useMemberPagedFeed<CommunityPost>({
    queryKey: communityMemberPostsKeys.feed(memberId),
    fetchPage: (cursor) => fetchCommunityMemberPostsPage(memberId, cursor),
    initialItems: initialPosts && withPinnedFirst(initialPosts, initialPinnedPost),
    initialMeta,
  });

  return { posts, ...pagination };
};

export type { UseCommunityMemberPostsOptions };
export { communityMemberPostsKeys, useCommunityMemberPosts };
